import React, { useEffect, useState } from 'react';
import { Table, Button, Form, Container, Row, Col, Card } from 'react-bootstrap';
import api from '../../services/api';
import { useSearchParams, useNavigate } from 'react-router-dom';
import Layout from '../../components/Layout';
import TopBar from '../../components/TopBar';
import { FaPrint, FaDownload } from 'react-icons/fa';
import { baseURL } from '../../services/api';
import PageHeader from '../../components/PageHeader';

const printStyles = `
  @media print {
    @page {
      size: A4 landscape;
      margin: 0.5cm;
    }
    body * {
      visibility: hidden;
      font-size: 14px !important;
    }
    .printable, .printable * {
      visibility: visible;
      font-size: 14px !important;
    }
    .printable {
      position: absolute;
      left: 0;
      top: 0;
      width: 100%;
      padding: 10px;
    }
    .no-print,
    .actions-column,
    .download-btn {
      display: none !important;
    }
    table {
      width: 100%;
      border-collapse: collapse;
      font-size: 14px !important;
    }
    th, td {
      border: 1px solid #000;
      padding: 4px 6px;
      font-size: 14px !important;
      line-height: 1.2;
    }
    th {
      white-space: nowrap;
    }
    .table-responsive {
      overflow: visible !important;
    }
  }
`;

const Payroll = () => {
  const [searchParams] = useSearchParams();
  const getCurrentMonth = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    return `${year}-${month}`;
  };
  
  const [month, setMonth] = useState(searchParams.get('month') || getCurrentMonth());
  const [departments, setDepartments] = useState([]);
  const [selectedDepartment, setSelectedDepartment] = useState('all');
  const [summary, setSummary] = useState([]);
  const [payrollStatus, setPayrollStatus] = useState([]);
  const [isGenerating, setIsGenerating] = useState(false);
  const [payrollGenerationStatus, setPayrollGenerationStatus] = useState({});
  const [generationStatus, setGenerationStatus] = useState('');
  const [currentStatus, setCurrentStatus] = useState(null);
  const [isStatusLoading, setIsStatusLoading] = useState(true);
  const navigate = useNavigate();

  const handleDownloadPayslip = (employeeId, month) => {
    // Open the download URL in a new tab
    const downloadUrl = `${baseURL}api/payslips/${employeeId}/${month}/download`;
    window.open(downloadUrl, '_blank');
  };

  // Extract unique departments from the summary data
  useEffect(() => {
    if (summary.length > 0) {
      const uniqueDepts = [];
      const deptMap = new Map();
      
      summary.forEach(item => {
        if (item.department && !deptMap.has(item.department.id)) {
          deptMap.set(item.department.id, true);
          uniqueDepts.push({
            id: item.department.id,
            name: item.department.name
          });
        }
      });
      
      console.log('Extracted departments:', uniqueDepts);
      setDepartments(uniqueDepts);
    }
  }, [summary]);

  useEffect(() => {
    // Fetch summary when month changes
    if (month) {
      fetchSummary();
    }
  }, [month]);

  const fetchSummary = () => {
    api.get(`/payrolls/monthly-summary?month=${month}`)
      .then(res => {
        console.log('Monthly summary data:', res.data);
        setSummary(res.data.data);
      })
      .catch(err => {
        console.error('Error fetching monthly summary:', err);
        setSummary([]);
      });
  };

  const handleDepartmentChange = (e) => {
    setSelectedDepartment(e.target.value);
  };

  const filteredSummary = selectedDepartment === 'all' 
    ? summary 
    : summary.filter(item => {
        // If no department is set, don't include in filtered results
        if (!item.department) return false;
        
        // Check if department matches the selected one
        return item.department.id == selectedDepartment || 
               item.department.name == selectedDepartment;
      });

  const handleMonthChange = (e) => {
    setMonth(e.target.value);
  };

  // Check the current status when month changes
  useEffect(() => {
    const checkStatus = async () => {
      setIsStatusLoading(true);
      try {
        const response = await api.get(`/payroll/status?month=${month}`);
        setCurrentStatus(response.data.data?.status || '');
      } catch (error) {
        console.error('Error checking status:', error);
        setCurrentStatus('');
      } finally {
        setIsStatusLoading(false);
      }
    };
    
    checkStatus();
  }, [month]);

  const handleGeneratePayroll = async () => {
    try {
      setIsGenerating(true);
      
      // Save the status as 'generated' for the selected month
      const response = await api.post('/payroll/status', {
        month: month,
        status: 'generated'
      });
      
      setGenerationStatus('Payroll generated successfully!');
      setCurrentStatus('generated');
      
      // Refresh the status
      const statusResponse = await api.get(`/payroll/status?month=${month}`);
      setPayrollStatus(statusResponse.data.status);
      
    } catch (error) {
      console.error('Error generating payroll:', error);
      setGenerationStatus('Failed to generate payroll. Please try again.');
    } finally {
      setIsGenerating(false);
    }
  };



  return (
    <Layout>
    <TopBar />

    <Container fluid className="mt-4" style={{ maxHeight: '85vh', overflowY: 'auto' }}>
      
   <Card className="mb-3">
  <Card.Body className="p-3">
    <Row className="g-3">
      <Col md={4}>
        <Form.Group className="mb-0">
          <Form.Label className="small mb-1">Select Month</Form.Label>
          <Form.Control type="month" value={month} onChange={handleMonthChange} size="sm" />
        </Form.Group>
      </Col>
      <Col md={4}>
        <Form.Group className="mb-0">
          <Form.Label className="small mb-1">Filter by Department</Form.Label>
          <Form.Select 
            value={selectedDepartment}
            onChange={handleDepartmentChange}
            disabled={departments.length === 0}
            size="sm"
          >
            <option value="all">All Departments</option>
            {departments.length > 0 ? (
              departments.map(dept => ({
                name: typeof dept === 'object' ? dept.name : dept,
                value: typeof dept === 'object' ? dept.id : dept
              })).map(({name, value}) => (
                <option key={value} value={value}>{name || 'No Department'}</option>
              ))
            ) : (
              <option disabled>No departments found</option>
            )}
          </Form.Select>
        </Form.Group>
      </Col>
      <Col md={4}>
        <div className="d-flex gap-2 justify-content-end">
          {!isStatusLoading && currentStatus !== 'generated' && (
            <Button 
              variant="outline-primary"
              onClick={handleGeneratePayroll}
              disabled={isGenerating}
            >
              {isGenerating ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                  Generating...
                </>
              ) : (
                'Generate Payroll'
              )}
            </Button>
          )}
          {generationStatus && (
            <div className="ms-2 d-inline-block align-middle">
              <span className="text-success">{generationStatus}</span>
            </div>
          )}
          <Button variant="primary" onClick={() => window.print()}>
            <FaPrint className="me-2" /> Print Payroll
          </Button>

          {/* <Button
  variant="primary"
  onClick={() => {
    const selectedMonth = month; // use current state
    api.post('/payslips/generate-bulk', { month: selectedMonth })
      .then(res => alert(res.data.message))
      .catch(err => alert("Failed to generate payslips"));
  }}
>
  Generate All Payslips
</Button> */}
        </div>
      </Col>
    </Row>
  </Card.Body>
</Card>


      {month && (
        <div>
         
          <Card className="printable">
          <Card.Body>
            <Table striped bordered hover responsive>
              <thead>
                <tr>
                  <th>#</th>
                  <th>Employee</th>
                  <th>Employee Code</th>
                  <th>Salary Type</th>
                  <th>Total Days</th>
                  <th>Total Hours</th>
                  <th>Overtime</th>
                  <th>Gross Salary</th>
                 
                  <th>Net Salary</th>
                  {currentStatus === "generated" &&
                    <th className="actions-column">Actions</th>
                  }
                
                </tr>
              </thead>
              <tbody>
                {filteredSummary.map((item, index) => (
                  <tr key={item.employee_id}>
                    <td>{index + 1} </td>
                    <td>{item.employee_name}</td>
                    <td>{item.emp_code}</td>
                    <td>{item.salary_type}</td>
                    <td>{item.total_days}</td>
                    <td>{item.total_hours}</td>
                    <td>{item.overtime_hours}</td>
                    <td>{item.gross_salary}</td>
                  
                    <td>
                      {item.net_salary}
                
                    </td>
                    {currentStatus === "generated" &&
                      <td className="actions-column">
                        <Button 
                          variant="outline-success" 
                          size="sm" 
                          className="ms-2 download-btn"
                          title="Download Payslip"
                          onClick={() => handleDownloadPayslip(item.employee_id, month)}
                        >
                          <FaDownload size={14} />
                        </Button>
                      </td>
                    }
                  </tr>
                ))}
              </tbody>
            </Table>
          </Card.Body>
        </Card>
      </div>
      )}
      <style>{printStyles}</style>
    </Container>
    </Layout>
  );
};

export default Payroll;
